import React, { useCallback, useEffect, useState } from 'react';
import './PreScanQuestions.scss';
import Logo from '../../assets/icons/logo-b.svg';
import ErrorDisplay from '../../components/BaseComponents/ErrorDisplay';
import PreScanQuestionnaireForm from '../../components/PreScan/PreScanQuestionnaireForm';
import { validatePreScanToken } from '../../api/preScan';
import { categorizeError, ErrorInfo } from '../../utils/errorHandler';

const ACCESS_DENIED_ERROR = categorizeError({
  response: {
    status: 403,
    data: { detail: "You don't have access to this form." },
  },
});

const getTokenFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('token');
};

const PreScanQuestions = () => {
  const [isTokenValidating, setIsTokenValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);

  const handleFormError = useCallback((error: ErrorInfo | null) => {
    setErrorInfo(error);
    if (error) {
      setIsTokenValid(false);
    }
  }, []);

  useEffect(() => {
    const validateTokenOnLoad = async () => {
      const token = getTokenFromURL();
      if (token) {
        sessionStorage.setItem('temp_token', token);
      }

      if (!token) {
        setErrorInfo(ACCESS_DENIED_ERROR);
        setIsTokenValid(false);
        setIsTokenValidating(false);
        return;
      }

      try {
        const response = await validatePreScanToken(token);

        if (response?.status !== 'success') {
          setErrorInfo(ACCESS_DENIED_ERROR);
          setIsTokenValid(false);
        } else {
          setErrorInfo(null);
          setIsTokenValid(true);
        }
      } catch (error: any) {
        setErrorInfo(categorizeError(error));
        setIsTokenValid(false);
      } finally {
        setIsTokenValidating(false);
      }
    };

    validateTokenOnLoad();
  }, []);

  if (isTokenValidating) {
    return (
      <div className='pre-scan-questions-container-page'>
        <div className='round-loader'>
          <div className='spinner' />
        </div>
      </div>
    );
  }

  if (errorInfo) {
    return (
      <div className='pre-scan-questions-container-page pre-scan-questions-error'>
        <ErrorDisplay errorInfo={errorInfo} />
      </div>
    );
  }

  if (!isTokenValid) {
    return null;
  }

  return (
    <div className='pre-scan-questions-wrapper'>
      <div className='pre-scan-questions-card'>
        <div className='pre-scan-questions-container'>
          <div className='d-flex align-items-center justify-content-center'>
            <img src={Logo} alt='logo' width={216} height={39} />
          </div>
          {isSubmitted ? (
            <div
              className='pre-scan-questions-description'
              style={{ textAlign: 'center', marginTop: '24px' }}
            >
              Thank you for completing your pre-scan questionnaire. Our clinical team will review
              your responses before your scan.
            </div>
          ) : (
            <>
              <div className='pre-scan-questions-description'>
                Please complete this form before your scan. Your answers confirm MRI safety, 
                inform your appointment, and give our clinical team the context they need to review 
                your results accurately. All fields marked with an asterisk are required.
              </div>
              <PreScanQuestionnaireForm
                setIsSubmitted={setIsSubmitted}
                onError={handleFormError}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreScanQuestions;
